import React from 'react';
import MetaTags from 'react-meta-tags';
import { connect } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import PropTypes from 'prop-types';
import './MyCurators.scss';
import icons from '../../static/icons';
import cn from 'classnames';
import {
  createCuratorGroup,
  updateCuratorGroup,
  deleteCuratorGroup,
  deleteCuratorGroupFavorite,
  createCuratorGroupUser,
  deleteCuratorGroupUser
} from '../../Actions/ShopActions';
import { openNewCircleModal } from '../../Actions/UIActions';
import { getOrderedCuratorGroups, getOrderedCuratorGroupFavorites } from '../../Helpers/user_helpers';
import { getNumRecentFindsForGroupId, getNumRecentFindsTotal } from '../../Helpers/shop_helpers';
import { getUrlParam, setUrlParam } from '../../Helpers/helpers';

import CuratorGroupCard from './CuratorGroupCard';
import ShopEmptyResults from './ShopEmptyResults';
import InstagramCSVGroupCreationModal from '../Modals/InstagramCSVGroupCreationModal';
import TrainingTooltip from '../General/TrainingTooltip';
const MyCurators = props => {
  const { shop, user, selectGroup } = props;

  // Get curator groups and favorites
  const curatorGroups = getOrderedCuratorGroups(user);
  const curatorGroupFavorites = getOrderedCuratorGroupFavorites(user);
  const numRecentFindsTotal = getNumRecentFindsTotal(shop);

  // Tabs and Visible Groups
  const tabs = [
    {
      variable: 'all',
      label: 'All',
      results: [...curatorGroups, ...curatorGroupFavorites]
    },
    {
      variable: 'groups',
      label: 'My Circles',
      results: curatorGroups
    },
    {
      variable: 'favorites',
      label: 'Favorited Circles',
      results: curatorGroupFavorites
    },
    {
      variable: 'with-new',
      label: 'With New Finds',
      badgeLabel: !numRecentFindsTotal ? null : numRecentFindsTotal > 100 ? '100+ New' : `${numRecentFindsTotal} New`,
      results: [...curatorGroups, ...curatorGroupFavorites].filter(group => getNumRecentFindsForGroupId(shop, group.id) > 0)
    }
  ];
  const [activeTab, setActiveTab] = React.useState(getUrlParam('tab') || 'all');
  const fullActiveTabObject = tabs.find(tab => tab.variable === activeTab);
  const visibleCuratorGroups = fullActiveTabObject?.results || [];
  const changeTab = tab => {
    setActiveTab(tab);
    setUrlParam('tab', tab);
    window.__ADD_EVENT__('Shop - My Curators - Change Tab', { tab });
  };

  // Connecting TikTok
  const [viewingInstagramCSVGroupCreationModal, setViewingInstagramCSVGroupCreationModal] = React.useState(false);
  const uploadFromInstagramCSV = () => {
    window.__ADD_EVENT__('Shop - Open Instagram CSV Upload Modal');
    setViewingInstagramCSVGroupCreationModal(true);
  };

  // Actions
  const createGroup = () =>
    props.openNewCircleModal({
      params: {},
      source: 'My Curators',
      closeCallback: () => {},
      submitCallback: () => {}
    });

  const header = 'My Circles';
  const seo_header = 'ShopMy | My Circles';
  const seo_description = 'Your own personal shops, curated by your favorite people.';

  return (
    <div className='my-curators-container'>
      <MetaTags>
        <title>{seo_header}</title>
        <meta property='og:title' content={seo_header} />
        <meta property='description' content={seo_description} />
        <meta property='og:description' content={seo_description} />
      </MetaTags>
      <div className='my-curators-header-container'>
        <div className='section-title-container'>
          <h1 className='section-title'>{header}</h1>
          <h2 className='section-subtitle'>{seo_description}</h2>
        </div>
        <div className='section-actions'>
          <div onClick={uploadFromInstagramCSV} className='action secondary create-group'>
            From Instagram
            <img src={icons.social.instagram.dark} alt='Instagram' />
          </div>
          <TrainingTooltip
            visible={curatorGroups.length === 0}
            trainingKey='create-circle-from-my-circles-tab'
            fadeInDelay={2_000}
            fadeOutDelay={10_000}
            message={<>Create your first Circle!</>}
            position='right'
            positionIfInitialOffScreen='bottom'
            numDismissalsToHide={1}
          >
            <div onClick={createGroup} className='action primary create-group'>
              Create Circle
              <FontAwesomeIcon icon={faPlus} />
            </div>
          </TrainingTooltip>
        </div>
      </div>
      <div className='my-curator-tabs-outer-container'>
        <div className='my-curator-tabs'>
          {tabs.map(tab => (
            <div key={tab.variable} className={cn('my-curator-tab', { active: activeTab === tab.variable })} onClick={() => changeTab(tab.variable)}>
              {tab.label}
              {tab.badgeLabel && <div className='badge-label'>{tab.badgeLabel}</div>}
            </div>
          ))}
        </div>
      </div>
      {visibleCuratorGroups.length ? (
        <div className={cn('my-curators', `total-${visibleCuratorGroups.length}`)}>
          {visibleCuratorGroups.map(object => {
            const isFavorite = object.curator_group;
            const favorite = isFavorite ? object : null;
            const group = isFavorite ? object.curator_group : object;
            return (
              <CuratorGroupCard
                key={group.id}
                favorite={favorite}
                group={group}
                user={user}
                shop={shop}
                openToTab='latest'
                selectGroup={selectGroup}
                deleteCuratorGroup={props.deleteCuratorGroup}
                deleteCuratorGroupFavorite={props.deleteCuratorGroupFavorite}
                showNumNew
              />
            );
          })}
        </div>
      ) : (
        <ShopEmptyResults
          user={user}
          isMyCuratorGroups={activeTab === 'all' || activeTab === 'groups'}
          isFavoritedCuratorGroups={activeTab === 'favorites'}
          isNewFindsCuratorGroups={activeTab === 'with-new'}
          createGroup={createGroup}
        />
      )}
      {viewingInstagramCSVGroupCreationModal && <InstagramCSVGroupCreationModal closeModal={() => setViewingInstagramCSVGroupCreationModal(false)} />}
    </div>
  );
};

MyCurators.propTypes = {
  // From Inside
  user: PropTypes.object.isRequired,
  shop: PropTypes.object.isRequired,
  createCuratorGroup: PropTypes.func.isRequired,
  deleteCuratorGroup: PropTypes.func.isRequired,
  deleteCuratorGroupFavorite: PropTypes.func.isRequired,
  openNewCircleModal: PropTypes.func.isRequired,

  // From Outside
  selectGroup: PropTypes.func.isRequired,
  selectGroupAndEdit: PropTypes.func.isRequired
};

const mapStateToProps = state => {
  const { user, shop } = state;
  return { user, shop };
};

export default connect(mapStateToProps, {
  createCuratorGroup,
  updateCuratorGroup,
  deleteCuratorGroup,
  deleteCuratorGroupFavorite,
  createCuratorGroupUser,
  deleteCuratorGroupUser,
  openNewCircleModal
})(MyCurators);
